import { NavigationView, NAVIGATION_VIEW_LABELS } from '@/shared/constants/navigation-view.enum';
import { Links } from '@/shared/constants/navegacao';
import {
  buildFolhasPorEstagiarioHref,
  buildFolhasPorMesHref,
} from '@/shared/navigation/navigation-folha-links.utils';
import { buildMovimentacoesPendentesEnviadasHref, buildMovimentacoesTiposHref } from '@/shared/navigation/navigation-movimentacoes-links.utils';
import type { NavigationPermissions } from './navigation-permissions.utils';

export type NavMenuItem = {
  label: string;
  to: string;
  title?: string;
  /** Abre em nova aba (URL absoluta, ex.: Metabase). */
  external?: boolean;
  /** Se omitido, o item é exibido quando a visão o inclui. */
  visible?: (p: NavigationPermissions) => boolean;
};

export type PainelGerencialMenuItem = {
  id: number;
  nome: string;
};

export type NavMenuDivider = {
  kind: 'divider';
  label: string;
};

export type NavMenuEntry = NavMenuItem | NavMenuDivider;

export function isNavMenuDivider(entry: NavMenuEntry): entry is NavMenuDivider {
  return 'kind' in entry && entry.kind === 'divider';
}

export type NavMenuGroup = {
  label: string;
  items: NavMenuEntry[];
};

export type NavMenuSection = {
  /** Título da seção na sidebar (ex.: "Defensor(a)"). */
  title: string;
  groups: NavMenuGroup[];
  /** Itens de raiz, sem submenu. */
  flatItems?: NavMenuItem[];
};

function item(
  label: string,
  to: string,
  opts?: Partial<NavMenuItem>,
): NavMenuItem {
  return { label, to, ...opts };
}

/** Consulta de vínculos/lotações — disponível para todos os perfis autenticados. */
const TABELAS_VINCULOS_ITEM: NavMenuItem = item('Tabelas de Vínculos', Links.Relatorio_Lotacoes, {
  title: 'Tabelas de vínculos e lotações.',
});

/** Grupo dinâmico: um item por painel liberado (`/paineis-gerenciais/:id`). */
function buildPaineisGerenciaisGroup(
  paineis: PainelGerencialMenuItem[],
): NavMenuGroup | null {
  if (!paineis.length) return null;
  return {
    label: 'Painéis Gerenciais',
    items: paineis.map((painel) =>
      item(painel.nome, `${Links.PaineisGerenciais_Detalhe}/${painel.id}`, {
        title: painel.nome,
      }),
    ),
  };
}

const FUNCOES_CARGOS_ITEM: NavMenuItem = item('Funções e Cargos', Links.Funcao_Index, {
  visible: (p) => p.colaboradores,
  title: 'Funções e cargos para colaboradores.',
});

const BLOQUEIOS_FERIADOS_ITEM: NavMenuItem = item('Bloqueios e Feriados', Links.Calendario_Index, {
  visible: (p) => p.folhas,
  title: 'Cadastro e edição de feriados e pontos facultativos.',
});

const CONSELHO_SUPERIOR_ITEM: NavMenuItem = item('Conselho Superior', Links.ConselhoSuperior_Index, {
  visible: (p) => p.conselhoSuperior,
  title: 'Composição do Conselho Superior por biênio.',
});

/** Cadastro unificado de pessoas (defensores, estagiários, colaboradores e demais perfis). */
const RECURSOS_HUMANOS_CADASTROS_GROUP: NavMenuGroup = {
  label: 'Recursos Humanos',
  items: [
    item('Cadastros de pessoas', Links.Pessoa_Index, {
      visible: (p) => p.cadastrosPessoas,
    }),
    FUNCOES_CARGOS_ITEM,
  ],
};

const LOCAIS_ATUACAO_GROUP: NavMenuGroup = {
  label: 'Locais de Atuação',
  items: [
    item('Vínculos', Links.Vinculos_Index, {
      visible: (p) => p.lotacoes,
    }),
    TABELAS_VINCULOS_ITEM,
  ],
};

const LOCAIS_ATUACAO_TABELAS_GROUP: NavMenuGroup = {
  label: 'Locais de Atuação',
  items: [TABELAS_VINCULOS_ITEM],
};

const FERIAS_EVENTOS_GROUP: NavMenuGroup = {
  label: 'Férias e eventos',
  items: [
    item('Resumo', Links.Eventos_Index, {
      visible: (p) => p.ferias || p.movimentos,
      title: 'Gestão de férias, abonos, faltas, licenças e afastamentos.',
    }),
    item('Cadastro de Ausências', Links.Acoes_Index, {
      visible: (p) => p.ferias || p.movimentos,
    }),
    item('Eventos institucionais', Links.EventoInstitucional_Index, {
      visible: (p) => p.ferias || p.movimentos,
    }),
    item('Regras de validação', Links.Regras, {
      visible: (p) => p.regras,
    }),
    item('Tabelas de Ações', Links.Relatorio_Ferias, {
      visible: (p) => p.ferias,
    }),
    item('Pontuações', Links.Pontuacoes_Index, {
      visible: (p) => p.ferias || p.movimentos,
    }),
  ],
};

const GESTAO_ESTAGIOS_GROUP: NavMenuGroup = {
  label: 'Gestão de Estágios',
  items: [
    item('Folhas de frequência', Links.Folha_Frequencia_Index, {
      visible: (p) => p.folhas,
    }),
    item('Pendências', Links.Folha_Frequencia_Pendencias, {
      visible: (p) => p.folhas,
    }),
    item('Instituições de Ensino', Links.Instituicoes_Index, {
      visible: (p) => p.estagiarios,
    }),
    item('Processos seletivos', Links.ProcessoSeletivo_Index, {
      visible: (p) => p.folhas,
    }),
  ],
};

const GESTAO_ESTAGIOS_FOLHAS_GROUP: NavMenuGroup = {
  label: 'Gestão de Estágios',
  items: [
    item('Folhas de frequência', Links.Folha_Frequencia_Index, {
      visible: (p) => p.folhas,
    }),
    item('Pendências', Links.Folha_Frequencia_Pendencias, {
      visible: (p) => p.folhas,
    }),
  ],
};

const ATIVIDADES_GROUP_ADMIN: NavMenuGroup = {
  label: 'Atividades',
  items: [
    item('Pendentes e Enviadas', buildMovimentacoesPendentesEnviadasHref(), {
      visible: (p) => p.movimentos,
      title: 'Painel de movimentações pendentes e enviadas do mês atual.',
    }),
    item('Cadastro de Atividades', Links.Movimentacoes_Index, {
      visible: (p) => p.movimentos,
    }),
    item('Pesquisa de Atividades', Links.Movimentacoes_Lista, {
      visible: (p) => p.movimentos,
      title: 'Pesquisa de atividades.',
    }),
    item('Tabelas de Atividades', Links.Relatorio_Movimentacoes, {
      visible: (p) => p.movimentos,
      title: 'Tabelas de atividades.',
    }),
    item('Tipos de Movimentações', buildMovimentacoesTiposHref(), {
      visible: (p) => p.movimentos,
      title: 'Cadastro de tipos de movimentação.',
    }),
  ],
};

const RELATORIOS_CADASTRO_GROUP: NavMenuGroup = {
  label: 'Cadastro de Relatórios',
  items: [
    item('Relatórios Mensais', Links.Relatorios, {
      visible: (p) => p.relatoriosAtividades,
      title: 'Criar, editar e enviar seus relatórios mensais.',
    }),
    item('Diretoria Regional', Links.Relatorios_Aprovacao, {
      visible: (p) => p.relatoriosDiretoria,
      title: 'Aprovação/reprovação dos relatórios na diretoria.',
    }),
    item('Corregedoria', Links.Relatorios_Corregedoria, {
      visible: (p) => p.relatoriosCorregedoria,
      title: 'Aval final e tramitação na Corregedoria.',
    }),
  ],
};

const RELATORIOS_SOLAR_ITEM = item('Relatórios do Solar (dev)', Links.Relatorios_Solar, {
  title: 'Consultas diretas ao Solar. Em desenvolvimento.',
  visible: () => false,
});

function comRelatorioSolar(grupo: NavMenuGroup): NavMenuGroup {
  return { ...grupo, items: [...grupo.items, RELATORIOS_SOLAR_ITEM] };
}

const ORGANIZACIONAIS_GROUP: NavMenuGroup = {
  label: 'Organizacionais',
  items: [
    item('Diretorias', Links.Diretoria_Index),
    item('Esferas de Atuação', Links.Atuacoes_Index),
    item('Varas/Centrais', Links.Varas_Index),
    item('Defensorias', Links.Defensoria_Index),
    item('Núcleos', Links.Nucleo_Index),
    item('Setores', Links.Setor_Index),
    BLOQUEIOS_FERIADOS_ITEM,
  ],
};

const LOCAIS_GROUP: NavMenuGroup = {
  label: 'Locais',
  items: [
    item('Comarcas', Links.Comarcas_Index),
    item('Distritos', Links.Distrito_Index),
    item('Fóruns/Unidades', Links.Predio_Index),
  ],
};

const ADMINISTRADOR_MENU: NavMenuSection[] = [
  {
    title: 'Administrador(a)',
    groups: [
      RECURSOS_HUMANOS_CADASTROS_GROUP,
      LOCAIS_ATUACAO_GROUP,
      FERIAS_EVENTOS_GROUP,
      ATIVIDADES_GROUP_ADMIN,
      comRelatorioSolar(RELATORIOS_CADASTRO_GROUP),
      GESTAO_ESTAGIOS_GROUP,
      {
        label: 'Organizacionais',
        items: [
          item('Diretorias', Links.Diretoria_Index, { visible: (p) => p.distritos }),
          CONSELHO_SUPERIOR_ITEM,
          item('Esferas de Atuação', Links.Atuacoes_Index, { visible: (p) => p.atuacao }),
          item('Varas/Centrais', Links.Varas_Index, { visible: (p) => p.vara }),
          item('Defensorias', Links.Defensoria_Index, { visible: (p) => p.defensoria }),
          item('Núcleos', Links.Nucleo_Index, { visible: (p) => p.nucleos }),
          item('Setores', Links.Setor_Index, { visible: (p) => p.forum }),
          BLOQUEIOS_FERIADOS_ITEM,
        ],
      },
      {
        label: 'Locais',
        items: [
          item('Comarcas', Links.Comarcas_Index, { visible: (p) => p.comarca }),
          item('Distritos', Links.Distrito_Index, { visible: (p) => p.distritos }),
          item('Fóruns/Unidades', Links.Predio_Index, { visible: (p) => p.forum }),
        ],
      },
      {
        label: 'Sistema',
        items: [
          item('Auditorias', Links.Auditorias_Index, { visible: (p) => p.logs }),
          item('Configurações', Links.Configuracoes_Index, { visible: (p) => p.logs }),
          item('Cadastro de Painéis', Links.PaineisGerenciais_Admin, {
            visible: (p) => p.administrador,
            title: 'Cadastro de painéis gerenciais do Metabase e vínculos de usuários.',
          }),
        ],
      },
    ],
  },
];

const CORREGEDORIA_MENU: NavMenuSection[] = [
  {
    title: 'Corregedoria',
    groups: [
      RECURSOS_HUMANOS_CADASTROS_GROUP,
      LOCAIS_ATUACAO_GROUP,
      FERIAS_EVENTOS_GROUP,
      {
        label: 'Atividades',
        items: [
          item('Pendentes e Enviadas', buildMovimentacoesPendentesEnviadasHref(), {
            visible: (p) => p.movimentos,
            title: 'Painel de movimentações pendentes e enviadas do mês atual.',
          }),
          item('Cadastro de Atividades', Links.Movimentacoes_Index, {
            visible: (p) => p.movimentos,
          }),
          item('Pesquisa de Atividades', Links.Movimentacoes_Lista, {
            visible: (p) => p.movimentos,
            title: 'Pesquisa de atividades.',
          }),
          item('Tabelas de Atividades', Links.Relatorio_Movimentacoes, {
            visible: (p) => p.movimentos,
            title: 'Tabelas de atividades.',
          }),
          item('Tipos de Movimentações', buildMovimentacoesTiposHref(), {
            visible: (p) => p.movimentos,
            title: 'Cadastro de tipos de movimentação.',
          }),
        ],
      },
      comRelatorioSolar(RELATORIOS_CADASTRO_GROUP),
      GESTAO_ESTAGIOS_GROUP,
      {
        label: 'Organizacionais',
        items: [CONSELHO_SUPERIOR_ITEM, ...ORGANIZACIONAIS_GROUP.items],
      },
      LOCAIS_GROUP,
    ],
  },
];

const DEFENSOR_MENU: NavMenuSection[] = [
  {
    title: 'Defensor(a)',
    groups: [
      LOCAIS_ATUACAO_TABELAS_GROUP,
      GESTAO_ESTAGIOS_FOLHAS_GROUP,
      comRelatorioSolar(RELATORIOS_CADASTRO_GROUP),
    ],
  },
];

const DEFENSOR_DIRETOR_MENU: NavMenuSection[] = [
  {
    title: 'Defensor(a)',
    groups: [
      LOCAIS_ATUACAO_GROUP,
      {
        label: 'Estagiários',
        items: [
          item('Folhas de frequência', Links.Folha_Frequencia_Index, {
            visible: (p) => p.folhas,
          }),
        ],
      },
      comRelatorioSolar(RELATORIOS_CADASTRO_GROUP),
    ],
  },
];

const ESTAGIARIO_MENU: NavMenuSection[] = [
  {
    title: 'Estagiário(a)',
    flatItems: [
      item('Minhas Folhas', Links.Estagiario_Minhas_Folhas, {
      }),
      item('Folha Atual', Links.Estagiario_Folha_Atual, {
      }),
    ],
    groups: [],
  },
];

const COLABORADOR_MENU: NavMenuSection[] = [
  {
    title: 'Colaborador(a)',
    groups: [
      LOCAIS_ATUACAO_TABELAS_GROUP,
      GESTAO_ESTAGIOS_FOLHAS_GROUP,
      RELATORIOS_CADASTRO_GROUP,
      ORGANIZACIONAIS_GROUP,
      LOCAIS_GROUP,
    ],
  },
];

/** Reavalia links dinâmicos (ex.: mês atual em “Folhas por mês”). */
function resolveSubcorregedoriaMenu(): NavMenuSection[] {
  return [
    {
      title: NAVIGATION_VIEW_LABELS[NavigationView.Subcorregedoria],
      groups: [
        RECURSOS_HUMANOS_CADASTROS_GROUP,
        LOCAIS_ATUACAO_GROUP,
        {
          label: 'Gestão de Estágios',
          items: [
            item('Folhas por mês', buildFolhasPorMesHref(), {
              visible: (p) => p.folhas,
              title: 'Folhas de frequência agrupadas por mês.',
            }),
            item('Folhas por estagiário', buildFolhasPorEstagiarioHref(), {
              visible: (p) => p.folhas,
              title: 'Folhas de frequência de um estagiário.',
            }),
            item('Pendências', Links.Folha_Frequencia_Pendencias, {
              visible: (p) => p.folhas,
            }),
            item('Processos seletivos', Links.ProcessoSeletivo_Index, {
              visible: (p) => p.folhas,
            }),
            item('Instituições de Ensino', Links.Instituicoes_Index, {
              visible: (p) => p.estagiarios,
            }),
          ],
        },
        comRelatorioSolar(RELATORIOS_CADASTRO_GROUP),
        ORGANIZACIONAIS_GROUP,
        LOCAIS_GROUP,
      ],
    },
  ];
}

const RELATORIOS_CONSULTA_DEFENSORES_GROUP: NavMenuGroup = {
  label: 'Relatórios de Atividades',
  items: [
    item('Relatórios por Defensor(a)', Links.Relatorios, {
      visible: (p) => p.relatoriosAtividades,
      title: 'Consulta dos relatórios mensais de um defensor.',
    }),
  ],
};

const RECURSOS_HUMANOS_MENU: NavMenuSection[] = [
  {
    title: 'Recursos Humanos',
    groups: [
      RECURSOS_HUMANOS_CADASTROS_GROUP,
      RELATORIOS_CONSULTA_DEFENSORES_GROUP,
      FERIAS_EVENTOS_GROUP,
      LOCAIS_ATUACAO_GROUP,
      ORGANIZACIONAIS_GROUP,
      LOCAIS_GROUP,
    ],
  },
];

const AUXILIAR_MENU: NavMenuSection[] = [
  {
    title: 'Auxiliar',
    groups: [
      RECURSOS_HUMANOS_CADASTROS_GROUP,
      LOCAIS_ATUACAO_GROUP,
      ORGANIZACIONAIS_GROUP,
      LOCAIS_GROUP,
    ],
  },
];

/** Utilitários restritos à visão de administrador. */
const UTILITARIOS_GROUP: NavMenuGroup = {
  label: 'Utilitários',
  items: [
    item('PDF DPE', Links.Utilitarios_PdfDpe, {
      visible: (p) => p.administrador,
      title: 'Editor de PDF da Defensoria (I Love DPE).',
    }),
  ],
};

/** Insere Utilitários e Painéis Gerenciais acima de Sistema (nessa ordem). */
function withUtilitariosEPaineisGroups(
  groups: NavMenuGroup[],
  paineis: PainelGerencialMenuItem[],
): NavMenuGroup[] {
  const trailing: NavMenuGroup[] = [UTILITARIOS_GROUP];
  const paineisGroup = buildPaineisGerenciaisGroup(paineis);
  if (paineisGroup) trailing.push(paineisGroup);
  const sistemaIdx = groups.findIndex((group) => group.label === 'Sistema');
  if (sistemaIdx < 0) return [...groups, ...trailing];
  return [
    ...groups.slice(0, sistemaIdx),
    ...trailing,
    ...groups.slice(sistemaIdx),
  ];
}

const GESTOR_RELATORIOS_MENU: NavMenuSection[] = [
  {
    title: NAVIGATION_VIEW_LABELS[NavigationView.GestorRelatorios],
    groups: [
      LOCAIS_ATUACAO_TABELAS_GROUP,
      ATIVIDADES_GROUP_ADMIN,
      comRelatorioSolar(RELATORIOS_CADASTRO_GROUP),
    ],
  },
];

const MENU_BY_VIEW: Record<NavigationView, NavMenuSection[]> = {
  [NavigationView.Administrador]: ADMINISTRADOR_MENU,
  [NavigationView.Corregedoria]: CORREGEDORIA_MENU,
  [NavigationView.Subcorregedoria]: [],
  [NavigationView.DefensorDiretor]: DEFENSOR_DIRETOR_MENU,
  [NavigationView.Defensor]: DEFENSOR_MENU,
  [NavigationView.Estagiario]: ESTAGIARIO_MENU,
  [NavigationView.Colaborador]: COLABORADOR_MENU,
  [NavigationView.RecursosHumanos]: RECURSOS_HUMANOS_MENU,
  [NavigationView.Auxiliar]: AUXILIAR_MENU,
  [NavigationView.GestorRelatorios]: GESTOR_RELATORIOS_MENU,
};

export function getNavigationMenuForView(
  view: NavigationView,
  permissions: NavigationPermissions,
  paineisGerenciais: PainelGerencialMenuItem[] = [],
): NavMenuSection[] {
  const sections =
    view === NavigationView.Subcorregedoria
      ? resolveSubcorregedoriaMenu()
      : (MENU_BY_VIEW[view] ?? []);
  const paineis = permissions.paineisGerenciais ? paineisGerenciais : [];
  return sections
    .map((section) => ({
      ...section,
      flatItems: (section.flatItems ?? []).filter(
        (navItem) => !navItem.visible || navItem.visible(permissions),
      ),
      groups: withUtilitariosEPaineisGroups(section.groups, paineis)
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (entry) =>
              isNavMenuDivider(entry) ||
              !entry.visible ||
              entry.visible(permissions),
          ),
        }))
        .filter((group) =>
          group.items.some((entry) => !isNavMenuDivider(entry)),
        ),
    }))
    .filter(
      (section) =>
        (section.flatItems?.length ?? 0) > 0 || section.groups.length > 0,
    );
}
